import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { useCart, groupCartRows } from "@/hooks/storefront/use-cart";
import { useCartPriceSync } from "@/hooks/storefront/use-cart-price-sync";
import { validateCartStock } from "@/lib/products.functions";
import { BundleCartGroup } from "@/components/storefront/BundleCartGroup";
import { formatPrice, productSlug } from "@/lib/format";
import { computeCartBundleDiscount } from "@/lib/mrbox";
import { ProductImage } from "@/components/storefront/ProductImage";
import { Trash2, Loader2 } from "lucide-react";
import { CrossSell } from "@/components/storefront/CrossSell";
import { SimilarProducts } from "@/components/storefront/SimilarProducts";
import { CartIssuesDialog, type CartIssue } from "@/components/storefront/CartIssuesDialog";
import { CartAsideSuggestions } from "@/components/storefront/CartAsideSuggestions";
import { useMrPandaZone } from "@/hooks/storefront/use-mrpanda-zone";
import { CartCouponTeaser } from "@/components/storefront/CartCouponTeaser";
import { KlarnaMessaging } from "@/components/storefront/KlarnaMessaging";

export const Route = createFileRoute("/cart")({
  head: () => ({ meta: [
      { property: "og:url", content: "https://mrpanda.gr/cart" },
      { title: "Καλάθι — MrPanda.gr" },
      { name: "description", content: "Δες και διαχειρίσου τα προϊόντα στο καλάθι αγορών σου στο MrPanda.gr πριν ολοκληρώσεις την παραγγελία." },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Καλάθι — MrPanda.gr" },
      { property: "og:description", content: "Διαχειρίσου τα προϊόντα και το σύνολο αγορών σου στο MrPanda.gr." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ] }),
  component: CartPage,
});

function CartPage() {
  const { items, setQty, remove, removeBundle, duplicateBundle, syncPrices, subtotal, count, depositTotal } =
    useCart();
  useCartPriceSync(items, syncPrices);
  const navigate = useNavigate();
  const zone = useMrPandaZone();
  const freeOver = zone?.serviceable ? zone.freeOver : null;
  const validate = useServerFn(validateCartStock);
  const [checking, setChecking] = useState(false);
  const [issues, setIssues] = useState<CartIssue[]>([]);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const bundleDiscount = computeCartBundleDiscount(items);
  const effectiveTotal = Math.max(0, subtotal - bundleDiscount);


  const handleCheckout = async () => {
    if (items.length === 0 || checking) return;
    setChecking(true);
    try {
      const res = await validate({
        data: { items: items.map((it) => ({ productId: it.productId, quantity: it.quantity })) },
      });
      if (res.ok) {
        navigate({ to: "/checkout" });
        return;
      }
      setIssues(res.issues as CartIssue[]);
      setIssuesOpen(true);
    } catch {
      toast.error("Κάτι πήγε στραβά κατά τον έλεγχο του καλαθιού. Δοκίμασε ξανά.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <StorefrontLayout hideCartSidebar>
      <div className="mx-auto max-w-none px-4 pb-12 pt-6">
        {/* Hero header */}
        <div className="mp-playful-panel relative mb-8 overflow-hidden rounded-3xl border border-border p-8 text-center shadow-sm">
          <div className="pointer-events-none absolute -left-3 top-3 select-none text-5xl opacity-20 animate-bounce">🐼</div>
          <div className="pointer-events-none absolute right-4 top-5 select-none text-3xl opacity-20" style={{ animationDelay: "0.5s" }}>🎁</div>
          <div className="mb-3 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">
            <span className="text-3xl">🛒</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">Το καλάθι μου</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {count > 0
              ? `Έχεις ${count} προϊόν${count === 1 ? "τ" : "τα"} στο καλάθι σου 🎉`
              : "Ο πάντα περιμένει τα ψώνια σου... 🐼"}
          </p>
        </div>

        {items.length === 0 ? (
          <div className="animate-pop-in mx-auto max-w-md rounded-3xl border border-border p-12 text-center mp-playful-panel">
            <div className="mb-3 text-6xl">🐼🛒</div>
            <h2 className="text-lg font-bold">Ουπς, το καλάθι είναι άδειο!</h2>
            <p className="mt-1 text-sm text-muted-foreground">Ο πάντα πεινάει για ψώνια... βοήθησέ τον! 🎁</p>
            <Link
              to="/"
              className="mp-bouncy mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm"
            >
              🛍️ Πάμε για ψώνια
            </Link>
          </div>
        ) : (
          <>
          <CartCouponTeaser subtotal={effectiveTotal} />
          <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
            <ul className="space-y-4">
              {groupCartRows(items).map((row, idx) => {
                if (row.kind === "bundle")
                  return (
                    <BundleCartGroup
                      key={row.key}
                      group={row}
                      size="lg"
                      onAdd={() => duplicateBundle(row.key)}
                      onRemoveOne={() => removeBundle(row.key)}
                      onRemoveAll={() => removeBundle(row.key)}
                    />
                  );
                const it = row.item;
                const onSale = it.originalPrice != null && it.originalPrice > it.price;
                return (
                <li
                  key={`${it.bundleId ?? ""}-${it.productId}`}
                  className={`relative flex gap-4 overflow-hidden rounded-2xl border bg-card p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md animate-fade-in ${
                    onSale ? "mp-gold-card border-yellow-400/60" : "border-border"
                  }`}
                  style={{ animationDelay: `${Math.min(idx * 80, 480)}ms`, animationFillMode: "backwards" }}
                >
                  {onSale && (
                    <>
                      <span className="mp-sparkle" style={{ top: "8px", left: "10px" }}>✨</span>
                      <span className="mp-sparkle" style={{ bottom: "10px", right: "14px", animationDelay: "0.7s" }}>⭐</span>
                    </>
                  )}
                  <Link
                    to="/p/$slug"
                    params={{ slug: productSlug(it.name, it.psId, it.productId) }}
                    className="relative block h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition-transform duration-500 hover:rotate-1 hover:scale-105 sm:h-28 sm:w-28"
                  >
                    <ProductImage
                      src={it.imageUrl}
                      alt={it.name}
                      width={160}
                      draggable={false}
                      className="img-fit"
                      fallback={<div className="grid h-full w-full place-items-center text-3xl">🐼</div>}
                    />
                  </Link>
                  <div className="flex min-w-0 flex-1 flex-col justify-between">
                    <div>
                      <Link to="/p/$slug" params={{ slug: productSlug(it.name, it.psId, it.productId) }} className="block break-words text-sm font-medium leading-snug hover:text-primary">{it.name}</Link>
                      <div className="mt-1 flex items-center gap-2">
                        {onSale && (
                          <span className="text-xs text-muted-foreground line-through">{formatPrice(it.originalPrice!)}</span>
                        )}
                        <span className="text-sm text-price font-bold">{formatPrice(it.price)}</span>
                        {onSale && (
                          <span className="rounded-full bg-yellow-400/20 px-2 py-0.5 text-[10px] font-bold text-yellow-700 dark:text-yellow-400">Προσφορά</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={() => setQty(it.productId, it.quantity - 1)} className="mp-bouncy grid h-8 w-8 place-items-center rounded-full border border-border text-sm font-bold">−</button>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={it.quantity}
                        onChange={(e) => {
                          const n = parseInt(e.target.value.replace(/\D/g, ""), 10);
                          setQty(it.productId, Number.isNaN(n) ? 1 : n);
                        }}
                        onBlur={(e) => {
                          if (!e.target.value.trim()) setQty(it.productId, 1);
                        }}
                        className="w-10 bg-transparent text-center text-sm outline-none font-semibold"
                        aria-label="Τεμάχια"
                      />
                      <button onClick={() => setQty(it.productId, it.quantity + 1)} disabled={(typeof it.stock === "number" && it.stock > 0 && it.quantity >= it.stock) || it.quantity >= 999} className="mp-bouncy grid h-8 w-8 place-items-center rounded-full border border-border text-sm font-bold disabled:opacity-40">+</button>
                      <button onClick={() => remove(it.productId)} aria-label="Αφαίρεση προϊόντος" title="Αφαίρεση προϊόντος" className="ml-2 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="shrink-0 self-center whitespace-nowrap text-base font-extrabold text-price">{formatPrice(it.price * it.quantity)}</div>
                </li>
                );
              })}
            </ul>
            <aside className="h-fit rounded-3xl border border-border p-6 shadow-sm mp-playful-panel lg:sticky lg:top-24">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-bold">🧾 Σύνοψη παραγγελίας</h2>
              {/* 🐼 Μπάρα προόδου δωρεάν μεταφορικών — μόνο όταν γνωρίζουμε
                  πραγματικό όριο (παράδοση MrPanda στη ζώνη του χρήστη). */}
              {freeOver != null ? (
                <div className="mb-5">
                  <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-700"
                      style={{ width: `${Math.min(100, (subtotal / freeOver) * 100)}%` }}
                    />
                    <span
                      aria-hidden
                      className="pointer-events-none absolute -top-1.5 text-base transition-all duration-700"
                      style={{ left: `calc(${Math.min(100, (subtotal / freeOver) * 100)}% - 10px)` }}
                    >
                      🐼
                    </span>
                  </div>
                  <p className="mt-2 text-center text-xs font-semibold">
                    {subtotal >= freeOver ? (
                      <span className="text-success">Ναι! Κέρδισες δωρεάν μεταφορικά με παράδοση MrPanda 🎉</span>
                    ) : (
                      <>Ακόμη <span className="text-price">{formatPrice(freeOver - subtotal)}</span> για δωρεάν μεταφορικά με παράδοση MrPanda 🚚</>
                    )}
                  </p>
                </div>
              ) : (
                <p className="mb-5 text-center text-xs text-muted-foreground">
                  🚚 Τα μεταφορικά υπολογίζονται στο checkout, ανάλογα με τον τρόπο και τη διεύθυνση παράδοσης.
                </p>
              )}
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-muted-foreground">Υποσύνολο</dt><dd className="font-semibold">{formatPrice(subtotal)}</dd></div>
                {bundleDiscount > 0 && (
                  <div className="flex justify-between"><dt className="flex items-center gap-1 text-success">📦 Έκπτωση πακέτων</dt><dd className="font-semibold text-success">−{formatPrice(bundleDiscount)}</dd></div>
                )}
                <div className="flex justify-between"><dt className="text-muted-foreground">Μεταφορικά</dt><dd className="text-right text-xs font-medium text-muted-foreground">Υπολογίζονται στο checkout</dd></div>
                <div className="flex justify-between border-t border-border pt-3 text-lg font-extrabold"><dt>Μερικό σύνολο</dt><dd className="text-price">{formatPrice(effectiveTotal)}</dd></div>
                {depositTotal > 0 && (
                  <div className="text-xs text-muted-foreground">🍾 Περιλαμβάνεται εγγυοδοσία φιαλών {formatPrice(depositTotal)}</div>
                )}
              </dl>

              <KlarnaMessaging amount={effectiveTotal} className="mt-4" />

              <button
                type="button"
                onClick={handleCheckout}
                disabled={checking}
                className="mp-bouncy mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-center font-semibold text-primary-foreground shadow-sm hover:opacity-90 disabled:opacity-60"
              >
                {checking ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Έλεγχος διαθεσιμότητας…
                  </>
                ) : (
                  <>Ολοκλήρωση παραγγελίας 🚀</>
                )}
              </button>
              <p className="mt-3 text-center text-xs text-muted-foreground">Πληρωμή με Viva.com, Klarna, IRIS ή τραπεζικό έμβασμα.</p>
              {/* Γεμίζει μόνο τον κενό χώρο δίπλα σε μεγάλα καλάθια */}
              <div className="hidden lg:block">
                <CartAsideSuggestions
                  fromProductIds={items.map((it) => it.productId)}
                  excludeProductIds={items.map((it) => it.productId)}
                  slots={Math.min(6, Math.max(0, (items.length - 4) * 2))}
                />
              </div>
            </aside>
            <div className="space-y-6 lg:col-span-2">
              <CrossSell
                fromProductIds={items.map((it) => it.productId)}
                excludeProductIds={items.map((it) => it.productId)}
                limit={6}
                title="Συμπλήρωσε το καλάθι σου 🧺"
              />
              <SimilarProducts
                fromProductIds={items.map((it) => it.productId)}
                excludeProductIds={items.map((it) => it.productId)}
                limit={6}
              />
            </div>
          </div>
          </>
        )}
        <CartIssuesDialog open={issuesOpen} onOpenChange={setIssuesOpen} issues={issues} />
      </div>

    </StorefrontLayout>
  );
}
